"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useId, useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { resolveQrSignDesign, type QrSignDesign } from "@/lib/qr-sign-maker/designs";
import { loadDecorationImage } from "@/lib/qr-sign-maker/decoration-images";
import type { FontKey, LayoutOp, QrSignContent } from "@/lib/qr-sign-maker/layout";
import type { QrSignFormat } from "@/lib/qr-sign-maker/pdf";
import { createRetryingLoader } from "@/lib/qr-sign-maker/retrying-loader";
import { sanitizePrintableText } from "@/lib/qr-sign-maker/text";
import { QR_SIGN_URL_MAX_LENGTH, parseSignUrl } from "@/lib/qr-sign-maker/url";
import type { WordingPreset, WordingPresetId } from "@/lib/qr-sign-maker/wording";

// The whole tool runs on the visitor's device: the link, the wording and the
// PDF never leave the browser tab, and nothing is saved.

type PdfModule = typeof import("@/lib/qr-sign-maker/pdf");
type MeasureText = Awaited<ReturnType<PdfModule["createPdfMeasure"]>>;
interface QrSignMakerProps {
  designs: QrSignDesign[];
  presets: WordingPreset[];
}
const PREVIEW_URL = "https://guest.gallery";
const HEADING_MAX_LENGTH = 60;
const BODY_MAX_LENGTH = 240;
const A4_VIEWBOX = "0 0 595.28 841.89";
const FORMATS: QrSignFormat[] = ["sign", "cards"];
const SVG_FONTS: Record<FontKey, {
  family: string;
  style: string;
  weight: number;
}> = {
  serifItalic: {
    family: "'Times New Roman', Times, serif",
    style: "italic",
    weight: 400
  },
  sans: {
    family: "Helvetica, Arial, sans-serif",
    style: "normal",
    weight: 400
  },
  sansBold: {
    family: "Helvetica, Arial, sans-serif",
    style: "normal",
    weight: 700
  }
};

// pdf-lib and qrcode arrive in this one lazy chunk. A failed load is not
// cached, so pressing the button again loads it again.
const loadPdfModule = createRetryingLoader(() => import("@/lib/qr-sign-maker/pdf"));
function fileSlug(label: string): string {
  return label.toLowerCase().normalize("NFKD").replace(/æ/g, "ae").replace(/ø/g, "o").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}
export function QrSignMaker({
  designs,
  presets
}: QrSignMakerProps): React.JSX.Element {
  const t = useTranslations("QrSignMaker");
  const fieldId = useId();
  const [rawUrl, setRawUrl] = useState("");
  const [hasTouchedUrl, setHasTouchedUrl] = useState(false);
  const [format, setFormat] = useState<QrSignFormat>("sign");
  const [presetId, setPresetId] = useState<WordingPresetId>(presets[0]?.id ?? "classic");
  const preset = presets.find(item => item.id === presetId) ?? presets[0];
  const [heading, setHeading] = useState(preset?.heading ?? "");
  const [body, setBody] = useState(preset?.body ?? "");
  const [designId, setDesignId] = useState<string>(designs[0]?.id ?? "");
  const design = designs.find(item_0 => item_0.id === designId) ?? resolveQrSignDesign(designId);
  const [isDownloading, setIsDownloading] = useState(false);
  const [hasDownloadError, setHasDownloadError] = useState(false);
  const [preview, setPreview] = useState<{
    ops: LayoutOp[];
    qrPath: (modules: boolean[][]) => string;
    framePath: PdfModule["framePath"];
  } | null>(null);
  const [measure, setMeasure] = useState<{
    fn: MeasureText;
    module: PdfModule;
  } | null>(null);
  const parsedUrl = useMemo(() => parseSignUrl(rawUrl), [rawUrl]);
  const urlError = !parsedUrl.ok && (hasTouchedUrl || rawUrl.trim().length > 0) ? parsedUrl.reason : null;
  const hasRemovedCharacters = sanitizePrintableText(heading).removed || sanitizePrintableText(body).removed;
  const content = useMemo<QrSignContent | null>(() => {
    if (!preset || !design) {
      return null;
    }
    return {
      url: parsedUrl.ok ? parsedUrl.url : PREVIEW_URL,
      heading,
      body,
      tasksTitle: preset.tasksTitle,
      tasks: preset.tasks,
      scanLabel: preset.scanLabel,
      design
    };
  }, [parsedUrl, heading, body, preset, design]);
  useEffect(() => {
    let isCancelled = false;
    loadPdfModule().then(async module => {
      const fn = await module.createPdfMeasure();
      if (!isCancelled) {
        setMeasure({
          fn,
          module
        });
      }
    }).catch(() => {
      if (!isCancelled) {
        setHasDownloadError(true);
      }
    });
    return () => {
      isCancelled = true;
    };
  }, []);
  useEffect(() => {
    if (!measure || !content) {
      return;
    }
    const timer = window.setTimeout(() => {
      setPreview({
        ops: measure.module.buildQrSignLayout(content, format, measure.fn),
        qrPath: measure.module.qrPath,
        framePath: measure.module.framePath
      });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [measure, content, format]);
  const choosePreset = (id: WordingPresetId): void => {
    const next = presets.find(item_1 => item_1.id === id);
    if (!next) {
      return;
    }
    setPresetId(id);
    setHeading(next.heading);
    setBody(next.body);
  };
  const download = async (): Promise<void> => {
    setHasTouchedUrl(true);
    if (!parsedUrl.ok || !content) {
      return;
    }
    setIsDownloading(true);
    setHasDownloadError(false);
    try {
      const module_0 = await loadPdfModule();
      if (!measure) {
        // The first load failed: bring the preview back too.
        setMeasure({
          fn: await module_0.createPdfMeasure(),
          module: module_0
        });
      }
      const bytes = await module_0.renderQrSignPdf({
        ...content,
        url: parsedUrl.url
      }, format, loadDecorationImage);
      const blob = new Blob([bytes as BlobPart], {
        type: "application/pdf"
      });
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `qr-${fileSlug(t(`format.${format}`))}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch {
      setHasDownloadError(true);
    } finally {
      setIsDownloading(false);
    }
  };
  const urlInputId = `${fieldId}-url`;
  const urlHelpId = `${fieldId}-url-help`;
  const headingId = `${fieldId}-heading`;
  const bodyId = `${fieldId}-body`;
  return <div className="grid gap-8 md:grid-cols-[minmax(0,1fr)_minmax(0,360px)] md:items-start">
      <div className="space-y-7">
        <div className="space-y-2">
          <Label htmlFor={urlInputId} className="text-base font-semibold text-[#2b231d]">
            {t("urlLabel")}
          </Label>
          <Input id={urlInputId} type="url" inputMode="url" autoComplete="off" autoCapitalize="none" autoCorrect="off" spellCheck={false} maxLength={QR_SIGN_URL_MAX_LENGTH * 4} placeholder={t("urlPlaceholder")} value={rawUrl} onChange={event => setRawUrl(event.target.value)} onBlur={() => setHasTouchedUrl(true)} aria-invalid={urlError ? true : undefined} aria-describedby={urlHelpId} className="h-12 text-base" />
          <div id={urlHelpId} aria-live="polite" className="text-sm leading-relaxed">
            {urlError ? <p className="text-red-700">
                {t(`errors.${urlError === "too-long" ? "tooLong" : urlError}`, {
              max: QR_SIGN_URL_MAX_LENGTH
            })}
              </p> : parsedUrl.ok ? <p className="text-[#5b5045]">
                {t("urlEncodedLabel")}{" "}
                <code className="break-all rounded bg-[#f4efe8] px-1 py-0.5 text-[13px] text-[#2b231d]">
                  {parsedUrl.url}
                </code>
              </p> : <p className="text-[#5b5045]">{t("urlHelp")}</p>}
          </div>
        </div>

        <fieldset className="space-y-2">
          <legend className="text-base font-semibold text-[#2b231d]">{t("formatLabel")}</legend>
          <div className="grid grid-cols-2 gap-2">
            {FORMATS.map(option => <label key={option} className={cn("cursor-pointer rounded-xl border p-3 text-left transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[#c2780f]", format === option ? "border-[#2b231d] bg-[#2b231d] text-white" : "border-[#e7e1d8] bg-white text-[#2b231d]")}>
                <input type="radio" name={`${fieldId}-format`} value={option} checked={format === option} onChange={() => setFormat(option)} className="sr-only" />
                <span className="block text-[15px] font-semibold">{t(`format.${option}`)}</span>
                <span className={cn("mt-0.5 block text-[13px] leading-snug", format === option ? "text-white/80" : "text-[#5b5045]")}>
                  {t(`formatHint.${option}`)}
                </span>
              </label>)}
          </div>
        </fieldset>

        <fieldset className="space-y-2">
          <legend className="text-base font-semibold text-[#2b231d]">{t("wordingLabel")}</legend>
          <div className="flex flex-wrap gap-2">
            {presets.map(item_2 => <label key={item_2.id} className={cn("cursor-pointer rounded-full border px-4 py-2 text-[15px] transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[#c2780f]", presetId === item_2.id ? "border-[#2b231d] bg-[#2b231d] text-white" : "border-[#e7e1d8] bg-white text-[#2b231d]")}>
                <input type="radio" name={`${fieldId}-wording`} value={item_2.id} checked={presetId === item_2.id} onChange={() => choosePreset(item_2.id)} className="sr-only" />
                {item_2.label}
              </label>)}
          </div>
          <div className="space-y-3 pt-2">
            <div className="space-y-1.5">
              <Label htmlFor={headingId} className="text-sm text-[#5b5045]">
                {t("headingLabel")}
              </Label>
              <Input id={headingId} value={heading} maxLength={HEADING_MAX_LENGTH} onChange={event_0 => setHeading(event_0.target.value)} className="h-11 text-base" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={bodyId} className="text-sm text-[#5b5045]">
                {t("bodyLabel")}
              </Label>
              <Textarea id={bodyId} value={body} rows={3} maxLength={BODY_MAX_LENGTH} onChange={event_1 => setBody(event_1.target.value)} className="text-base" />
            </div>
            {preset && preset.tasks.length > 0 ? <div className="rounded-lg bg-[#fbf6ee] px-3 py-2 text-sm text-[#5b5045]">
                <p className="font-semibold text-[#2b231d]">{preset.tasksTitle}</p>
                <ul className="mt-1 list-disc pl-5">
                  {preset.tasks.map(task => <li key={task}>{task}</li>)}
                </ul>
              </div> : null}
            {hasRemovedCharacters ? <p className="text-sm text-[#8a5a1c]" role="status">
                {t("removedCharacters")}
              </p> : null}
          </div>
        </fieldset>

        <fieldset className="space-y-2">
          <legend className="text-base font-semibold text-[#2b231d]">{t("designLabel")}</legend>
          <div className="flex flex-wrap gap-4">
            {designs.map(item_3 => <label key={item_3.id} className="flex cursor-pointer flex-col items-center gap-1.5 rounded-xl has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[#c2780f] has-[:focus-visible]:ring-offset-2">
                <input type="radio" name={`${fieldId}-design`} value={item_3.id} checked={design?.id === item_3.id} onChange={() => setDesignId(item_3.id)} className="sr-only" />
                <span aria-hidden="true" className={cn("flex h-12 w-12 items-center justify-center rounded-full border-2 transition-shadow", design?.id === item_3.id ? "border-[#2b231d] shadow-[0_0_0_3px_#fff_inset]" : "border-[#e7e1d8]")} style={{
              backgroundColor: item_3.background
            }}>
                  <span className="font-serif text-lg italic leading-none" style={{
                color: item_3.text
              }}>
                    Aa
                  </span>
                </span>
                <span className={cn("text-[13px]", design?.id === item_3.id ? "font-semibold text-[#2b231d]" : "text-[#5b5045]")}>
                  {t(`designNames.${item_3.id}`)}
                </span>
              </label>)}
          </div>
        </fieldset>
      </div>

      <div className="space-y-4 md:sticky md:top-24">
        <p className="text-base font-semibold text-[#2b231d]">{t("previewLabel")}</p>
        <div className="mx-auto w-full max-w-[360px] overflow-hidden rounded-md border border-[#e7e1d8] bg-white shadow-[0_12px_32px_rgba(43,35,29,0.12)]">
          <svg viewBox={A4_VIEWBOX} role="img" aria-label={`${t("previewLabel")}: ${t(`format.${format}`)}`} className="block h-auto w-full">
            <rect x="0" y="0" width="595.28" height="841.89" fill="#ffffff" />
            {preview ? <PreviewOps ops={preview.ops} qrPath={preview.qrPath} framePath={preview.framePath} isPlaceholder={!parsedUrl.ok} /> : null}
          </svg>
        </div>
        <Button type="button" size="lg" onClick={() => void download()} disabled={isDownloading} className="h-12 w-full rounded-full bg-[#2b231d] text-base text-white hover:bg-[#433a33]">
          {isDownloading ? <Loader2 aria-hidden="true" className="mr-2 h-5 w-5 animate-spin" /> : <Download aria-hidden="true" className="mr-2 h-5 w-5" />}
          {isDownloading ? t("downloading") : t("download")}
        </Button>
        {hasDownloadError ? <p className="text-sm text-red-700" role="alert">
            {t("downloadError")}
          </p> : null}
        <p className="text-sm leading-relaxed text-[#5b5045]">{t("privacyNote")}</p>
      </div>
    </div>;
}
function PreviewOps(t0) {
  const $ = _c(11);
  const {
    ops,
    qrPath,
    framePath,
    isPlaceholder
  } = t0;
  let t1;
  if ($[0] !== framePath || $[1] !== isPlaceholder || $[2] !== ops || $[3] !== qrPath) {
    let t2;
    if ($[5] !== framePath || $[6] !== isPlaceholder || $[7] !== qrPath) {
      t2 = (op, index) => {
        switch (op.kind) {
          case "rect":
          case "card":
            {
              return <rect key={index} x={op.x} y={op.y} width={op.w} height={op.h} rx={op.kind === "rect" ? op.radius : undefined} fill={op.color} />;
            }
          case "frame":
            {
              return <path key={index} d={framePath(op)} transform={`translate(${op.x} ${op.y})`} fill="none" stroke={op.color} strokeWidth={op.width} strokeOpacity={op.opacity} />;
            }
          case "dot":
            {
              return <circle key={index} cx={op.cx} cy={op.cy} r={op.r} fill={op.color} />;
            }
          case "image":
            {
              return <image key={index} href={op.src} x={op.x} y={op.y} width={op.w} height={op.h} opacity={op.opacity} preserveAspectRatio="none" />;
            }
          case "line":
            {
              return <line key={index} x1={op.x1} y1={op.y1} x2={op.x2} y2={op.y2} stroke={op.color} strokeWidth={op.width} />;
            }
          case "text":
            {
              const font = SVG_FONTS[op.font];
              return <text key={index} x={op.x} y={op.y} fontSize={op.size} fontFamily={font.family} fontStyle={font.style} fontWeight={font.weight} fill={op.color} textAnchor={op.align === "center" ? "middle" : "start"}>{op.text}</text>;
            }
          case "qr":
            {
              return <path key={index} d={qrPath(op.modules)} transform={`translate(${op.x} ${op.y}) scale(${op.size / op.modules.length})`} fill={op.color} opacity={isPlaceholder ? 0.15 : 1} />;
            }
        }
      };
      $[5] = framePath;
      $[6] = isPlaceholder;
      $[7] = qrPath;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    t1 = ops.map(t2);
    $[0] = framePath;
    $[1] = isPlaceholder;
    $[2] = ops;
    $[3] = qrPath;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[9] !== t1) {
    t2 = <>{t1}</>;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}